import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import assessmentService, {
  AssessmentDetail,
  Evaluation,
  EvaluationData,
  EvaluationDetail,
  EvaluationUrl,
  INITIAL,
  MetadataOption,
  REBA_CSV,
  RebaMetadataOptions,
  RULA_CSV,
} from "src/services/assessment/assessmentService";
import { PieChart } from "src/services/assessment/types/pieChart";
import { PieChartData } from "src/services/assessment/types/pieChartData";
import FileService from "src/services/fileService";
import PercentagePerBodyPartChartDataService from "src/services/assessment/percentagePerBodyPartChartDataService";
import { BodyPartJointAngle } from "src/services/assessment/types/bodyPartJointAngle";
import CsvToJsonService from "src/services/csvToJsonService";
import AssessmentService from "src/services/assessment/assessmentService";

export type DataDeepDive = {
  percentagePerBodyPart: PieChart;
  // can add the property for line graph here when that is done
};

const initialPercentagePerBodyPart = {
  leg: [] as PieChartData[],
  trunk: [] as PieChartData[],
  neck: [] as PieChartData[],
  upperArm: [] as PieChartData[],
  lowerArm: [] as PieChartData[],
};

export type assessmentDetailState = {
  assessmentId: string;
  assessmentName: string;
  selectTab: string;
  dataDeepDiveTab: boolean;
  initialEvaluationScoreLoaded: boolean;
  initialEvaluationVideoLoaded: boolean;
  followUpEvaluationScoreLoaded: boolean;
  followUpEvaluationVideoLoaded: boolean;
  assessmentDetail: AssessmentDetail;
  initialEvaluationUrl: EvaluationUrl;
  followupEvaluationUrl: EvaluationUrl;
  initialFallbackUrl: EvaluationUrl;
  followupFallbackUrl: EvaluationUrl;
  initialEvaluationDetail: EvaluationDetail;
  initialEvaluationData: EvaluationData;
  followupEvaluationData: EvaluationData;
  followupEvaluationDetail: EvaluationDetail;
  initialEvaluationDeepDive: DataDeepDive;
  followupEvaluationDeepDive: DataDeepDive;
  activityScoreOptions: MetadataOption[];
  summaryReportOnly: boolean;
};

const initialEvaluationDetailState = {
  evaluationId: "",
  owner: "",
  status: "",
  errorMessage: "",
  cliendId: "",
  assessmentId: "",
  forceLoadSelection: "",
  couplingSelection: "",
  activitySelections: [],
  rightWristAwkwardPosition: -1,
  leftWristAwkwardPosition: -1,
  rightWristBentTwisted: 0,
  leftWristBentTwisted: 0,
  isShockOrRapidBuildUpForce: 0,
  isNeckTwisted: 0,
  isRightArmSupportedOrPersonLeaning: 0,
  isLeftArmSupportedOrPersonLeaning: 0,
  isTrunkTwisted: 0,
  comment: "",
  highestRebaScore: 0,
  statusUpdateDate: 0,
  armForceLoadSelection: "", //
  armMuscleUseSelection: "",
  highestScore: 0,
  isLeftLowerArmAcrossMidlineOrOutToSide: 0,
  isLegFeetNotSupported: -1,
  isRightLowerArmAcrossMidlineOrOutToSide: 0,
  leftWristTwistPosition: -1,
  lowerBodyForceLoadSelection: "",
  lowerBodyMuscleUseSelection: "",
  rightWristTwistPosition: -1,
};

const initialEvaluationUrlState = {
  preSignedUrlForBiomechanics: "",
  preSignedUrlForDemoVideo: "",
  preSignedUrlForRebaScores: "",
  preSignedUrlForAssessmentScores: "",
};

const initialEvaluationState: Evaluation = {
  activity: 0,
  assessmentId: "",
  clientId: "",
  coupling: 0,
  evaluationId: "",
  forceLoad: 0,
  highestRebaScore: 0,
  isShockOrRapidBuildUpForce: 0,
  leftWristAwkwardPosition: -1,
  leftWristBentTwisted: 0,
  owner: "",
  rightWristAwkwardPosition: -1,
  rightWristBentTwisted: 0,
  status: "",
  statusUpdateDate: 0,
  errorMessage: "NONE",
};
const initialAssessmentDetailState = {
  activity: "",
  assessmentId: "",
  clientId: "",
  country: "",
  initialEvaluation: initialEvaluationState,
  followupEvaluation: initialEvaluationState,
  organization: "",
  owner: "",
  processPath: "",
  region: "",
  site: "",
  siteType: "",
  subOrganization: "",
  type: "",
};

const initialEvaluationData = {
  biomechanics: {},
  rebaScores: [],
  rulaScores: [],
  videoBlob: null,
};

const initialState: assessmentDetailState = {
  assessmentId: "",
  assessmentName: "",
  selectTab: INITIAL,
  initialEvaluationScoreLoaded: false,
  initialEvaluationVideoLoaded: false,
  followUpEvaluationScoreLoaded: false,
  followUpEvaluationVideoLoaded: false,
  dataDeepDiveTab: true,
  assessmentDetail: initialAssessmentDetailState,
  initialEvaluationUrl: initialEvaluationUrlState,
  followupEvaluationUrl: initialEvaluationUrlState,
  initialFallbackUrl: initialEvaluationUrlState,
  followupFallbackUrl: initialEvaluationUrlState,
  initialEvaluationData: initialEvaluationData,
  initialEvaluationDetail: initialEvaluationDetailState,
  followupEvaluationData: initialEvaluationData,
  followupEvaluationDetail: initialEvaluationDetailState,
  initialEvaluationDeepDive: {
    percentagePerBodyPart: initialPercentagePerBodyPart,
  },
  followupEvaluationDeepDive: {
    percentagePerBodyPart: initialPercentagePerBodyPart,
  },
  activityScoreOptions: [],
  summaryReportOnly: false,
};

export const getAssessmentDetailById = createAsyncThunk(
  "assessment/getAssessmentDetailById",
  async (payload: { assessmentId: string; clientId: string }, { getState }) => {
    const { data } = await assessmentService.getAssessmentDetailById({
      owner: payload.clientId,
      assessmentId: payload.assessmentId,
    });
    return data;
  }
);

export const getEvaluationDetailById = createAsyncThunk(
  "assessment/getEvaluationDetailById",
  async (
    payload: { evaluationId: string; evaluationType: string; clientId: string },
    {}
  ) => {
    const { data } = await assessmentService.getEvaluationDetailById({
      owner: payload.clientId,
      evaluationId: payload.evaluationId,
    });
    return { ...data, evaluationType: payload.evaluationType };
  }
);

export const getPreSignedUrlForDownload = createAsyncThunk(
  "assessment/PreSignedUrlForDownload",
  async (
    payload: { evaluationId: string; evaluationType: string; clientId: string },
    { getState }
  ) => {
    const { data } = await assessmentService.getPreSignedUrlForDownload({
      owner: payload.clientId,
      evaluationId: payload.evaluationId,
    });
    return { ...data, evaluationType: payload.evaluationType };
  }
);

export const downloadCSVToJson = createAsyncThunk(
  "assessment/CSV",
  async (
    payload: { preSignedUrl: string; fallbackUrl?: string; evaluationType: string; csvType: string },
    { getState }
  ) => {
    let csvBlob;
    try {
      const response = await fetch(payload.preSignedUrl, { method: "GET" });
      if (!response.ok) throw new Error("Download failed");
      csvBlob = await response.blob();
    } catch (error) {
      if (payload.fallbackUrl) {
        csvBlob = await FileService.downloadFile(payload.fallbackUrl);
      } else {
        throw error;
      }
    }
    const csvJson = await CsvToJsonService.convertCsvToJson(csvBlob);
    return {
      csvJson: csvJson,
      evaluationType: payload.evaluationType,
      csvType: payload.csvType,
    };
  }
);

export const downloadVideoToBlob = createAsyncThunk(
  "assessment/Video",
  async (
    payload: { preSignedUrl: string; fallbackUrl?: string; evaluationType: string },
    { getState }
  ) => {
    let blob;
    let usedFallback = false;
    try {
      const response = await fetch(payload.preSignedUrl, { method: "GET" });
      if (!response.ok) throw new Error("Download failed");
      blob = await response.blob();
    } catch (error) {
      if (payload.fallbackUrl) {
        blob = await FileService.downloadFile(payload.fallbackUrl);
        usedFallback = true;
      } else {
        throw error;
      }
    }
    const data = await blob.arrayBuffer();
    return {
      videoBlob: data,
      evaluationType: payload.evaluationType,
      usedFallback,
      fallbackUrl: payload.fallbackUrl,
    };
  }
);
export const getRebaMetadataOptions = createAsyncThunk(
  "assessment/getMetadataOptions",
  async () => {
    const payload = {
      metadataType: "REBA",
    };
    const { data } = await AssessmentService.getMetadataOptions(payload);
    // Parse the nested JSON string
    const parsedData: RebaMetadataOptions = JSON.parse(data.metadata);

    return parsedData;
  }
);

const { reducer, actions } = createSlice({
  name: "assessmentDetailSlice",
  initialState,
  reducers: {
    setAssessmentDetailId: (state, action) => {
      state.assessmentId = action.payload;
    },
    setSelectTab: (state, action) => {
      state.selectTab = action.payload;
    },
    setDataDeepDiveTab: (state, action) => {
      state.dataDeepDiveTab = action.payload;
    },
    setEvaluationScoreLoad: (state, action) => {
      if (action.payload === INITIAL) {
        state.initialEvaluationScoreLoaded = true;
      } else {
        state.followUpEvaluationScoreLoaded = true;
      }
    },
    resetAssessmentDetail: () => {
      return initialState;
    },
    setSummaryReportOnly: (state, { payload }) => {
      state.summaryReportOnly = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAssessmentDetailById.fulfilled, (state, { payload }) => {
      if (payload.assessmentDetail.hasOwnProperty("followupEvaluation")) {
        state.assessmentDetail = payload.assessmentDetail;
      } else {
        state.assessmentDetail = {
          ...payload.assessmentDetail,
          followupEvaluation: state.assessmentDetail.followupEvaluation,
        };
      }
    });
    builder.addCase(
      getPreSignedUrlForDownload.fulfilled,
      (state, { payload }) => {
        const fallbackUrl = {
          preSignedUrlForBiomechanics: payload.preSignedUrlForBiomechanics,
          preSignedUrlForDemoVideo: payload.preSignedUrlForDemoVideo,
          preSignedUrlForRebaScores: payload.preSignedUrlForRebaScores,
          preSignedUrlForAssessmentScores: payload.preSignedUrlForAssessmentScores,
        };
        const primaryUrl = {
          preSignedUrlForBiomechanics: payload.preSignedUrlForErgoNetBiomechanics,
          preSignedUrlForDemoVideo: payload.preSignedUrlForErgoNetDemoVideo,
          preSignedUrlForRebaScores: payload.preSignedUrlForErgoNetAssessmentScores,
          preSignedUrlForAssessmentScores: payload.preSignedUrlForErgoNetAssessmentScores,
        };
        if (payload.evaluationType === INITIAL) {
          state.initialEvaluationUrl = primaryUrl;
          state.initialFallbackUrl = fallbackUrl;
        } else {
          state.followupEvaluationUrl = primaryUrl;
          state.followupFallbackUrl = fallbackUrl;
        }
      }
    );
    builder.addCase(getEvaluationDetailById.fulfilled, (state, { payload }) => {
      if (payload.evaluationType === INITIAL) {
        state.initialEvaluationDetail = payload.evaluationDetail;
      } else {
        state.followupEvaluationDetail = payload.evaluationDetail;
      }
    });
    builder.addCase(downloadCSVToJson.fulfilled, (state, { payload }) => {
      if (payload.csvType === REBA_CSV) {
        const rebaScores = payload.csvJson.map((e) => {
          return {
            timeStamp: e.time_stamp,
            rebaScore: e.REBA_score,
            neckScore: e.neck_score,
            trunkScore: e.trunk_score,
            legScore: e.leg_score,
            upperArmScore: e.upperArm_score,
            lowerArmScore: e.lowerArm_score,
            wristScore: e.wrist_score,
            loading: e.loading,
            coupling: e.coupling,
          };
        });
        if (payload.evaluationType === INITIAL) {
          state.initialEvaluationData.rebaScores = rebaScores;
        } else {
          state.followupEvaluationData.rebaScores = rebaScores;
        }
      } else if (payload.csvType === RULA_CSV) {
        const rulaScores = payload.csvJson.map((e) => {
          return {
            timeStamp: e.time_stamp,
            rulaScore: e.RULA_score,
            neckScore: e.neck_score,
            trunkScore: e.trunk_score,
            legScore: e.leg_score,
            upperArmScore: e.upperArm_score,
            lowerArmScore: e.lowerArm_score,
            wristScore: e.wrist_score,
          };
        });
        if (payload.evaluationType === INITIAL) {
          state.initialEvaluationData.rulaScores = rulaScores;
        } else {
          state.followupEvaluationData.rulaScores = rulaScores;
        }
      } else {
        let bodyPartJointAngles =
          assessmentService.getBodyPartJointAnglesFromCsvJson(payload.csvJson);
        // Filter out nan body part joint angles. neckSag is not particularly signigicant.
        // If one body part joint angle is nan (eg: neckSag) then they are all (rShoulderSag, trunkSag, etc.)
        // nan.
        bodyPartJointAngles = bodyPartJointAngles.filter(
          ({ neckSag }) => !isNaN(parseFloat(neckSag))
        );
        const percentagePerBodyPartData =
          getPercentagePerBodyPartData(bodyPartJointAngles);
        if (payload.evaluationType === INITIAL) {
          state.initialEvaluationDeepDive.percentagePerBodyPart =
            percentagePerBodyPartData;
        } else {
          state.followupEvaluationDeepDive.percentagePerBodyPart =
            percentagePerBodyPartData;
        }
      }
    });
    builder.addCase(downloadVideoToBlob.fulfilled, (state, { payload }) => {
      const videoBlob = new Blob([payload.videoBlob], { type: "video/mp4" });
      if (payload.evaluationType === INITIAL) {
        state.initialEvaluationData.videoBlob = videoBlob;
        if (payload.usedFallback && payload.fallbackUrl) {
          state.initialEvaluationUrl.preSignedUrlForDemoVideo = payload.fallbackUrl;
        }
      } else {
        state.followupEvaluationData.videoBlob = videoBlob;
        if (payload.usedFallback && payload.fallbackUrl) {
          state.followupEvaluationUrl.preSignedUrlForDemoVideo = payload.fallbackUrl;
        }
      }
    });
    builder.addCase(getRebaMetadataOptions.fulfilled, (state, { payload }) => {
      state.activityScoreOptions = payload.activityScoreOptions;
    });
  },
});

const getPercentagePerBodyPartData = (
  bodyPartJointAngles: BodyPartJointAngle[]
) => {
  return {
    neck: PercentagePerBodyPartChartDataService.getChartDataForNeck(
      bodyPartJointAngles
    ),
    trunk:
      PercentagePerBodyPartChartDataService.getChartDataForTrunk(
        bodyPartJointAngles
      ),
    upperArm:
      PercentagePerBodyPartChartDataService.getChartDataForUpperArm(
        bodyPartJointAngles
      ),
    lowerArm:
      PercentagePerBodyPartChartDataService.getChartDataForLowerArm(
        bodyPartJointAngles
      ),
    leg: PercentagePerBodyPartChartDataService.getChartDataForLeg(
      bodyPartJointAngles
    ),
  };
};

export const {
  setAssessmentDetailId,
  setSelectTab,
  setDataDeepDiveTab,
  setEvaluationScoreLoad,
  resetAssessmentDetail,
  setSummaryReportOnly,
} = actions;

export default reducer;
